import React, { useEffect, useState } from "react";
import Button from "react-bootstrap/Button";
import Modal from "react-bootstrap/Modal";
import Form from "react-bootstrap/Form";
import axiosInstance from "../../../../hooks/axiosInstance";
import { toast } from "react-toastify";
import { Spinner } from "react-bootstrap";
import { COUNTRY_CODES } from "../../../../constants/countryCodes";

export default function EditUserMngt({
  showEdit,
  closeModal,
  refreshPage,
  userDetails,
}) {
  const [validationError, setValidationError] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [roles, setRoles] = useState([]);
  const [selectedRole, setSelectedRole] = useState("");
  const [selectedUser, setSelectedUser] = useState("");
  const [userType, setUserType] = useState([]);
  const countryCodes = COUNTRY_CODES;

  // Fetch roles and user types
  useEffect(() => {
    const fetchRolesAndPermissions = async () => {
      try {
        const userTypeResponse = await axiosInstance.get(`/user/auth/get_user_type`);
        const rolesResponse = await axiosInstance.get("/user/roles");
        const filteredRoles = rolesResponse.data.data.roles.data.filter(
          (role) => role.name !== "Customers"
        );

        setUserType(userTypeResponse.data.data.user_type || []);
        setRoles(filteredRoles);
      } catch (error) {
        console.error("Error fetching roles or permissions:", error);
        toast.error("Error fetching roles or user types.");
      }
    };

    fetchRolesAndPermissions();
  }, []);

  // Prepopulate form with userDetails
  const [formData, setFormData] = useState({
    first_name: "",
    last_name: "",
    email: "",
    phone: "",
    country_code: "+1", // Default to USA
    role: "",
    user_type_id: "",
  });

  useEffect(() => {
    if (userDetails) {
      const phone = userDetails.phone || "";
      let country_code = "+1"; // Default to USA
      let phone_number = phone;

      // Find matching country code
      const matchedCountry = countryCodes.find((country) =>
        phone.startsWith(country.code)
      );
      if (matchedCountry) {
        country_code = matchedCountry.code;
        phone_number = phone.slice(country_code.length);
      } else if (phone.startsWith("+")) {
        const possibleCode = phone.match(/^\+\d{1,4}/)?.[0];
        if (possibleCode) {
          country_code = possibleCode;
          phone_number = phone.slice(possibleCode.length);
        }
      }

      setFormData({
        first_name: userDetails.first_name || "",
        last_name: userDetails.last_name || "",
        email: userDetails.email || "",
        phone: phone_number,
        country_code,
        role: userDetails.roles[0]?.name || "",
        user_type_id: userDetails.user_type_id || "",
      });

      if (userDetails.roles.length > 0) {
        setSelectedRole(userDetails.roles[0].id);
      }
      if (userDetails.user_type_id) {
        setSelectedUser(userDetails.user_type_id);
      }
    }
  }, [userDetails, countryCodes]);

  const resetForm = () => {
    setFormData({
      first_name: "",
      last_name: "",
      email: "",
      phone: "",
      country_code: "+1",
      role: "",
      user_type_id: "",
    });
    setSelectedRole("");
    setSelectedUser("");
    setValidationError({});
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === "phone") {
      // Allow only numbers for phone
      const numericValue = value.replace(/[^0-9]/g, "");
      setFormData((prev) => ({ ...prev, phone: numericValue }));
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
  };

  const handleClose = () => {
    resetForm();
    closeModal();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      const payload = {
        first_name: formData.first_name,
        last_name: formData.last_name,
        phone: `${formData.country_code}${formData.phone}`,
        role: selectedRole,
        user_type_id: selectedUser,
      };

      const response = await axiosInstance.put(`/user/editUser/${userDetails.id}`, payload);

      if (response.data.success) {
        toast.success("User updated successfully!");
        refreshPage();
        handleClose();
      } else {
        toast.error(response.data.message || "Failed to update user.");
      }
    } catch (error) {
      console.error("Error updating user:", error);
      if (error.response && error.response.data.errors) {
        setValidationError(error.response.data.errors);
        toast.error("Failed to update user.");
      } else {
        toast.error("An error occurred while updating the user.");
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="CreateCSP">
      <Modal show={showEdit} onHide={handleClose} dialogClassName="modal-lg">
        <Form onSubmit={handleSubmit}>
          <Modal.Header closeButton>
            <Modal.Title>Edit User and Role</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Form.Group className="mb-3" controlId="formFirstName">
              <div>
                <h6>First Name</h6>
              </div>
              <Form.Control
                type="text"
                placeholder="Enter..."
                name="first_name"
                value={formData.first_name}
                onChange={handleChange}
                required
              />
              {validationError.first_name && (
                <small className="text-danger my-1">
                  {validationError.first_name[0]}
                </small>
              )}
            </Form.Group>

            <Form.Group className="mb-3" controlId="formLastName">
              <div>
                <h6>Last Name</h6>
              </div>
              <Form.Control
                type="text"
                placeholder="Enter..."
                name="last_name"
                value={formData.last_name}
                onChange={handleChange}
                required
              />
              {validationError.last_name && (
                <small className="text-danger my-1">
                  {validationError.last_name[0]}
                </small>
              )}
            </Form.Group>

            <Form.Group className="mb-3" controlId="formEmail">
              <div>
                <h6>Email</h6>
              </div>
              <Form.Control
                type="email"
                placeholder="Enter..."
                name="email"
                value={formData.email}
                onChange={handleChange}
                readOnly
                style={{ backgroundColor: "#f0f0f0", color: "#6c757d" }}
              />
              {validationError.email && (
                <small className="text-danger my-1">
                  {validationError.email[0]}
                </small>
              )}
            </Form.Group>

            <Form.Group className="mb-3" controlId="formPhoneNumber">
              <div>
                <h6>Phone Number</h6>
              </div>
              <div className="input-group">
                <select
                  name="country_code"
                  value={formData.country_code}
                  onChange={handleChange}
                  className="form-control"
                  style={{ width: "40%" }}
                  disabled={isLoading}
                >
                  {countryCodes.length === 0 ? (
                    <option>Loading...</option>
                  ) : (
                    countryCodes.map((country) => (
                      <option key={country.code} value={country.code}>
                        {country.label}
                      </option>
                    ))
                  )}
                </select>
                <Form.Control
                  type="tel"
                  placeholder="Enter phone number..."
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  required
                />
              </div>
              {validationError.phone && (
                <small className="text-danger my-1">
                  {validationError.phone[0]}
                </small>
              )}
            </Form.Group>

            <Form.Group className="mb-3" controlId="formUserType">
              <div>
                <h6>User Type</h6>
              </div>
              <Form.Select
                name="user_type_id"
                value={selectedUser}
                onChange={(e) => setSelectedUser(e.target.value)}
                required
              >
                <option value="">-- Select User Type --</option>
                {userType.map((user) => (
                  <option key={user.id} value={user.id}>
                    {user.user_type_name}
                  </option>
                ))}
              </Form.Select>
              {validationError.user_type_id && (
                <small className="text-danger my-1">
                  {validationError.user_type_id[0]}
                </small>
              )}
            </Form.Group>

            <Form.Group className="mb-3" controlId="formRole">
              <div>
                <h6>Role</h6>
              </div>
              <Form.Select
                name="role"
                value={selectedRole}
                onChange={(e) => setSelectedRole(e.target.value)}
                required
              >
                <option value="">-- Select Role --</option>
                {roles.map((role) => (
                  <option key={role.id} value={role.id}>
                    {role.name}
                  </option>
                ))}
              </Form.Select>
              {validationError.role && (
                <small className="text-danger my-1">
                  {validationError.role[0]}
                </small>
              )}
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={handleClose}>
              Close
            </Button>
            <Button
              style={{ backgroundColor: "#c4281b", color: "#fff" }}
              type="submit"
              disabled={isLoading}
            >
              {isLoading ? (
                <>
                  <Spinner
                    as="span"
                    animation="border"
                    size="sm"
                    role="status"
                    aria-hidden="true"
                  />{" "}
                  Updating...
                </>
              ) : (
                "Update"
              )}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </div>
  );
}